import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Button, Multiplier, Text, SegmentedButtons } from '@Sage/hoi-poi-ui';

import ModalHoi from 'components/ModalHoi';
import HtmlPreviewFrame from 'containers/components/HtmlPreviewFrame';
import { REG_EX_EMAIL } from 'constants/Constants';
import { getLiteral } from 'utils/getLiteral';
import { successToast, errorToast } from 'utils/toast';
import { sendTestEmail } from 'services/MailingService';

import './styles.scss';

function getEmailFromMultiplierRow(row) {
    if (row == null) {
        return '';
    }
    if (typeof row === 'string') {
        return row;
    }
    if (typeof row === 'object' && !Array.isArray(row)) {
        const v = row.email;
        return v == null ? '' : String(v);
    }
    return '';
}

function normalizeDefaultRecipients(defaultRecipient) {
    if (defaultRecipient == null || defaultRecipient === '') {
        return [''];
    }
    if (Array.isArray(defaultRecipient)) {
        const emails = defaultRecipient
            .map((r) => getEmailFromMultiplierRow(r).trim())
            .filter(Boolean);
        return emails.length > 0 ? emails : [''];
    }
    const single = String(defaultRecipient).trim();
    return single ? [single] : [''];
}

const CampaignPreviewTestModal = memo(
    ({
        isOpen,
        onRequestClose,
        from,
        subject,
        previewText,
        htmlPreview,
        defaultRecipient,
        onSendTestEmail,
    }) => {
        const [previewMode, setPreviewMode] = useState('web');
        const [recipients, setRecipients] = useState(() =>
            normalizeDefaultRecipients(defaultRecipient),
        );
        const [recipientFieldErrors, setRecipientFieldErrors] = useState([]);
        const [isLoading, setIsLoading] = useState(false);

        const emailRegex = useMemo(() => new RegExp(REG_EX_EMAIL.source, 'i'), []);
        const hasSender = Boolean(from);

        useEffect(() => {
            if (!isOpen) {
                return;
            }

            setRecipients(normalizeDefaultRecipients(defaultRecipient));
            setRecipientFieldErrors([]);
        }, [isOpen, defaultRecipient]);

        const handleClose = useCallback(() => {
            setRecipientFieldErrors([]);
            onRequestClose?.();
        }, [onRequestClose]);

        const handlePreviewModeChange = useCallback((mode) => {
            setPreviewMode(mode);
        }, []);

        const handleRecipientsChange = useCallback((values) => {
            setRecipients(values);
            setRecipientFieldErrors([]);
        }, []);

        const validateRecipients = useCallback(() => {
            if (!recipients || recipients.length === 0) {
                setRecipientFieldErrors([getLiteral('helptext_required_field')]);
                return false;
            }

            const fieldErrors = recipients.map((r) => {
                const trimmed = getEmailFromMultiplierRow(r).trim();
                if (!trimmed) {
                    return getLiteral('helptext_required_field');
                }
                if (!emailRegex.test(trimmed)) {
                    return getLiteral('error_invalid_email_format');
                }
                return '';
            });

            const hasAnyError = fieldErrors.some(Boolean);
            setRecipientFieldErrors(hasAnyError ? fieldErrors : []);
            return !hasAnyError;
        }, [emailRegex, recipients]);

        const handleSendTestEmail = useCallback(async () => {
            if (!validateRecipients()) {
                return;
            }

            const trimmedEmails = recipients
                .map((r) => getEmailFromMultiplierRow(r).trim())
                .filter(Boolean);

            const payload = {
                to: trimmedEmails,
                from,
                subject,
                previewText,
                body: htmlPreview,
            };

            setIsLoading(true);
            sendTestEmail(payload)
                .then(() => {
                    successToast({
                        text: getLiteral('label_toast_marketing_campaigns_send_email_success'),
                    });
                    onSendTestEmail?.(trimmedEmails);
                })
                .catch(() => {
                    errorToast({ text: getLiteral('error_generalerror') });
                })
                .finally(() => setIsLoading(false));
        }, [
            from,
            htmlPreview,
            onSendTestEmail,
            previewText,
            recipients,
            subject,
            validateRecipients,
        ]);

        return (
            <ModalHoi
                className={classNames('campaign-preview-test-modal', {
                    'campaign-preview-test-modal--no-sender': !hasSender,
                })}
                isOpen={isOpen}
                onRequestClose={handleClose}
                title={getLiteral(
                    hasSender
                        ? 'label_marketing_campaigns_preview_and_test'
                        : 'label_marketing_campaigns_email_preview_modal',
                )}
                useHeader
                useCornerClose
                size="full"
            >
                <div className="campaign-preview-test-modal__content">
                    <div className="campaign-preview-test-modal__preview-column">
                        <div className="campaign-preview-test-modal__preview__header">
                            {hasSender && (
                                <div className="campaign-preview-test-modal__header-left">
                                    <div className="campaign-preview-test-modal__row">
                                        <Text type="body" bold>
                                            {`${getLiteral('label_from')}:`}
                                        </Text>
                                        <Text type="body">{from || '-'}</Text>
                                    </div>
                                    <div className="campaign-preview-test-modal__row">
                                        <Text type="body" bold>
                                            {`${getLiteral('label_subject')}:`}
                                        </Text>
                                        <Text type="body">{subject || '-'}</Text>
                                    </div>
                                </div>
                            )}
                            <div
                                className={classNames('campaign-preview-test-modal__header-right', {
                                    'campaign-preview-test-modal__header-right--full': !hasSender,
                                })}
                            >
                                <SegmentedButtons
                                    options={[
                                        { name: 'web', icon: 'desktop' },
                                        { name: 'mobile', icon: 'mobile' },
                                    ]}
                                    value={previewMode}
                                    onChange={handlePreviewModeChange}
                                />
                            </div>
                        </div>

                        <div
                            className={classNames('campaign-preview-test-modal__html-preview', {
                                'campaign-preview-test-modal__html-preview--mobile':
                                    previewMode === 'mobile',
                            })}
                        >
                            <HtmlPreviewFrame
                                html={htmlPreview}
                                title={getLiteral('label_marketing_campaigns_preview_and_test')}
                            />
                        </div>
                    </div>

                    {hasSender && (
                        <div className="campaign-preview-test-modal__form-column">
                            <Text type="h6">
                                {getLiteral(
                                    'label_marketing_campaigns_preview_test_recipient_heading',
                                )}
                            </Text>
                            <Text type="body" className="campaign-preview-test-modal__description">
                                {getLiteral(
                                    'label_marketing_campaigns_preview_test_recipient_desc',
                                )}
                            </Text>

                            <Multiplier
                                isFullWidth
                                labelMode="vertical"
                                schema={{
                                    label: `${getLiteral('label_recipient')}*`,
                                    name: 'email',
                                    type: 'text',
                                    placeholder: getLiteral('placeholder_enter_email_address'),
                                }}
                                value={recipients}
                                onChange={handleRecipientsChange}
                                error={recipientFieldErrors}
                                buttonLabel={getLiteral('label_add_email')}
                                max={4}
                            />

                            <Button
                                className="campaign-preview-test-modal__send-test-email-button"
                                type="primary"
                                onClick={handleSendTestEmail}
                                isLoading={isLoading}
                            >
                                {getLiteral('action_marketing_campaigns_send_test_email')}
                            </Button>
                        </div>
                    )}
                </div>
            </ModalHoi>
        );
    },
);

CampaignPreviewTestModal.displayName = 'CampaignPreviewTestModal';

CampaignPreviewTestModal.propTypes = {
    isOpen: PropTypes.bool,
    onRequestClose: PropTypes.func,
    from: PropTypes.string,
    subject: PropTypes.string,
    previewText: PropTypes.string,
    htmlPreview: PropTypes.string,
    defaultRecipient: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    onSendTestEmail: PropTypes.func,
};

export default CampaignPreviewTestModal;
